import React from "react";
import styled from "@emotion/styled";

import { Text } from "@components/core/Text";
import { Modal } from "src/design-system/components/Modal/Modal";
import { fontP1Regular, fontP4Regular } from "src/design-system/styles/fonts";
import BackArrow from "@components/refactored-survey/images/back-arrow.svg";
import { compassColors } from "src/utils/styling";

const ModalWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const BackButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const BackArrowIcon = styled.img`
  width: 18px;
  height: 16px;
`;

const ContentArea = styled.div`
  padding-top: 36px;
`;

const Title = styled.h2`
  ${fontP1Regular}
  margin: 0 0 16px;
  text-align: left;
  text-transform: uppercase;
`;

const StrongEm = ({ children }: { children?: React.ReactNode }) => (
  <strong>
    <em>{children}</em>
  </strong>
);

const Intro = styled.p`
  ${fontP4Regular}
  line-height: 28px;
  margin: 0 0 16px;
`;

const ItemList = styled.ol`
  ${fontP4Regular}
  line-height: 28px;
  margin: 0;
  padding-left: 1.5em;
`;

const Item = styled.li`
  margin: 0 0 16px;
  padding-left: 0.25em;

  &:last-child {
    margin-bottom: 0;
  }
`;

const EmailLink = styled.a`
  color: ${compassColors.denim};
  text-decoration: underline;
`;

const SUPPORT_EMAIL = "admin@belugahealth.com";

const ATTESTATION_ITEM_KEYS = [
  "outOfPocket",
  "noReimbursement",
  "voluntaryAgreement",
  "confirmationOfUnderstanding",
  "medicareBeneficiary",
] as const;

const TEXT_COMPONENTS = {
  strong: <strong />,
  strongEm: <StrongEm />,
  EmailLink: <EmailLink href={`mailto:${SUPPORT_EMAIL}`} />,
};

type MedicareAttestationModalProps = {
  onClose: () => void;
};

export function MedicareAttestationModal({
  onClose,
}: MedicareAttestationModalProps) {
  return (
    <Modal onClose={onClose}>
      <ModalWrapper>
        <BackButton
          type="button"
          onClick={onClose}
          aria-label="Back"
          data-cy="medicare-attestation-modal-back"
        >
          <BackArrowIcon src={BackArrow} alt="" />
        </BackButton>
        <ContentArea>
          <Title>
            <Text i18nKey="payment:terms:medicareAttestation:modalTitle" />
          </Title>
          <Intro>
            <Text
              i18nKey="payment:terms:medicareAttestation:intro"
              components={TEXT_COMPONENTS}
            />
          </Intro>
          <ItemList>
            {ATTESTATION_ITEM_KEYS.map((itemKey) => (
              <Item key={itemKey}>
                <Text
                  i18nKey={`payment:terms:medicareAttestation:items:${itemKey}`}
                  components={TEXT_COMPONENTS}
                />
              </Item>
            ))}
          </ItemList>
        </ContentArea>
      </ModalWrapper>
    </Modal>
  );
}
